import React, { useState, useEffect } from 'react';
import {
  FaMoneyBillWave, FaCreditCard, FaBuilding, FaUser, FaKey,
  FaExclamationTriangle, FaCheckCircle, FaSpinner, FaWallet,
  FaShieldAlt, FaInfoCircle, FaArrowLeft, FaSave, FaRupeeSign,
  FaUniversity, FaIdCard, FaLock
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const InfluencerWithdrawalRequest = () => {
  const [form, setForm] = useState({
    accountHolder: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    amount: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [minWithdrawal, setMinWithdrawal] = useState(500); // Default, will be updated
  const [loadingBalance, setLoadingBalance] = useState(true);

  // Fetch wallet balance and minimum withdrawal amount
  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        const walletResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/influencer/wallet/balance`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (walletResponse.data.success) {
          setWalletBalance(walletResponse.data.balance || 0);
        }

        const pricingResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/ad/pricing`
        );
        if (pricingResponse.data.success) {
          // Note: Backend might use same key for all users
          setMinWithdrawal(pricingResponse.data.pricing?.minimumWithdrawAmountForReporter || 500);
        }
      } catch (error) {
        console.error('Error fetching wallet data:', error);
      } finally {
        setLoadingBalance(false);
      }
    };
    fetchWalletData();
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case 'accountHolder':
        if (!value.trim()) return 'Required';
        if (!/^[A-Za-z\s]+$/.test(value)) return 'Alphabets only';
        return '';
      case 'accountNumber':
        if (!value.trim()) return 'Required';
        if (!/^\d+$/.test(value)) return 'Digits only';
        if (value.length < 9 || value.length > 18) return '9-18 digits';
        return '';
      case 'ifscCode':
        if (!value.trim()) return 'Required';
        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) return 'Invalid format';
        return '';
      case 'bankName':
        if (!value.trim()) return 'Required';
        if (!/^[A-Za-z\s]+$/.test(value)) return 'Alphabets only';
        return '';
      case 'amount':
        if (!value.trim()) return 'Required';
        const amt = parseFloat(value);
        if (isNaN(amt) || amt <= 0) return 'Invalid amount';
        if (amt < minWithdrawal) return `Min ₹${minWithdrawal}`;
        if (amt > walletBalance) return 'Insufficient balance';
        if (amt > 100000) return 'Max ₹1,00,000';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === 'ifscCode') formattedValue = value.toUpperCase();

    setForm({ ...form, [name]: formattedValue });
    setErrors({ ...errors, [name]: validateField(name, formattedValue) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.keys(form).forEach(key => {
      newErrors[key] = validateField(key, form[key]);
    });
    setErrors(newErrors);

    if (Object.values(newErrors).some(err => err !== '')) {
      toast.error('Please fix errors');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/influencer/withdrawal/request`,
        form,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        toast.success('Request sent!');
        setForm({ accountHolder: '', accountNumber: '', ifscCode: '', bankName: '', amount: '' });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (name) => `w-full pl-9 pr-4 py-2 text-sm border ${errors[name] ? 'border-red-500 bg-red-50' : 'border-slate-200'} rounded-xl focus:ring-2 focus:ring-purple-500 outline-none transition-all`;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Compact Header */}
      <div className="bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 px-4 pt-6 pb-20 text-white">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <FaMoneyBillWave className="w-4 h-4 text-purple-300" />
            </div>
            <div>
              <h1 className="text-base font-black text-white">Withdraw Funds</h1>
              <p className="text-purple-300 text-[10px] font-bold uppercase tracking-widest">Wallet Management</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[9px] font-black text-purple-400 uppercase tracking-widest">Available</p>
            <p className="text-xl font-black text-white">
              {loadingBalance ? <FaSpinner className="animate-spin inline w-4 h-4" /> : `₹${walletBalance.toFixed(2)}`}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="max-w-md mx-auto px-3 -mt-12 pb-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">

          {/* Quick Stats/Info Bar */}
          <div className="flex border-b border-slate-100 bg-slate-50/50">
            <div className="flex-1 px-4 py-3 border-r border-slate-100 text-center">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Min Limit</p>
              <p className="text-xs font-black text-slate-700">₹{minWithdrawal}</p>
            </div>
            <div className="flex-1 px-4 py-3 text-center">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Processing</p>
              <p className="text-xs font-black text-slate-700">2-3 Days</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Account Details Group */}
            <div className="space-y-3">
              <div className="relative">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Account Holder</label>
                <div className="absolute left-3 top-[34px] text-slate-400">
                  <FaUser className="w-3.5 h-3.5" />
                </div>
                <input
                  name="accountHolder"
                  value={form.accountHolder}
                  onChange={handleChange}
                  className={inputCls('accountHolder')}
                  placeholder="Enter full name"
                  required
                />
                {errors.accountHolder && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.accountHolder}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Account No.</label>
                  <div className="absolute left-3 top-[34px] text-slate-400">
                    <FaIdCard className="w-3.5 h-3.5" />
                  </div>
                  <input
                    name="accountNumber"
                    value={form.accountNumber}
                    onChange={handleChange}
                    className={inputCls('accountNumber')}
                    placeholder="9-18 digits"
                    maxLength="18"
                    required
                  />
                  {errors.accountNumber && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.accountNumber}</p>}
                </div>
                <div className="relative">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">IFSC Code</label>
                  <div className="absolute left-3 top-[34px] text-slate-400">
                    <FaKey className="w-3.5 h-3.5" />
                  </div>
                  <input
                    name="ifscCode"
                    value={form.ifscCode}
                    onChange={handleChange}
                    className={inputCls('ifscCode')}
                    placeholder="SBIN00..."
                    maxLength="11"
                    required
                  />
                  {errors.ifscCode && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.ifscCode}</p>}
                </div>
              </div>

              <div className="relative">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Bank Name</label>
                <div className="absolute left-3 top-[34px] text-slate-400">
                  <FaUniversity className="w-3.5 h-3.5" />
                </div>
                <input
                  name="bankName"
                  value={form.bankName}
                  onChange={handleChange}
                  className={inputCls('bankName')}
                  placeholder="e.g. State Bank of India"
                  required
                />
                {errors.bankName && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.bankName}</p>}
              </div>
            </div>

            <div className="h-px bg-slate-100 my-2" />

            {/* Amount Section */}
            <div className="relative">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Withdrawal Amount</label>
              <div className="absolute left-3 top-[34px] text-slate-400">
                <FaRupeeSign className="w-3.5 h-3.5" />
              </div>
              <input
                name="amount"
                type="number"
                value={form.amount}
                onChange={handleChange}
                className={inputCls('amount')}
                placeholder={`Min ₹${minWithdrawal}`}
                required
              />
              {errors.amount && <p className="text-[10px] font-bold text-red-500 mt-1 ml-1">{errors.amount}</p>}
            </div>

            <button
              type="submit"
              disabled={loading || loadingBalance || walletBalance < minWithdrawal}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl text-sm font-black transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-100 disabled:opacity-50 disabled:shadow-none"
            >
              {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
              {loading ? 'SUBMITTING...' : 'REQUEST WITHDRAWAL'}
            </button>
          </form>

          {/* Security & Support Footer */}
          <div className="px-5 py-4 bg-slate-50/60 border-t border-slate-100 space-y-3">
            <div className="flex items-start gap-2">
              <FaShieldAlt className="w-3 h-3 text-green-500 mt-0.5" />
              <p className="text-[10px] text-slate-500 font-bold leading-relaxed">
                Your bank details are encrypted and stored securely. Ensure the account name matches your profile.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {['24/7 Support', 'Secure Bank Transfer', 'TDS Compliant'].map(item => (
                <span key={item} className="px-2 py-0.5 bg-white border border-slate-200 text-[9px] font-black text-slate-400 uppercase tracking-widest rounded-md">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Verification Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 text-center max-w-xs mx-4 shadow-2xl">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-purple-600 rounded-full animate-spin mx-auto mb-4" />
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Processing</h3>
            <p className="text-xs text-slate-500 mt-2">Securing your withdrawal request...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfluencerWithdrawalRequest;
